import { Link } from "react-router-dom";
import { motion, useMotionValue, useSpring, useTransform, useReducedMotion } from "framer-motion";
import { fileUrl, gbp } from "@/lib/api";
import { TrustBadge } from "@/components/TrustBadge";
import { Gauge, Fuel, Calendar, MapPin, ImageOff, Star } from "lucide-react";
import { CompareButton } from "@/components/compare/CompareButton";
import { setDragging, snapshot } from "@/lib/compareTray";

function useTilt() {
  const reduce = useReducedMotion();
  const x = useMotionValue(0.5);
  const y = useMotionValue(0.5);
  const rotateX = useSpring(useTransform(y, [0, 1], [6, -6]), { stiffness: 200, damping: 20 });
  const rotateY = useSpring(useTransform(x, [0, 1], [-6, 6]), { stiffness: 200, damping: 20 });
  const onMove = (e) => {
    if (reduce || e.pointerType !== "mouse") return;
    const r = e.currentTarget.getBoundingClientRect();
    x.set((e.clientX - r.left) / r.width);
    y.set((e.clientY - r.top) / r.height);
  };
  const onLeave = () => { x.set(0.5); y.set(0.5); };
  return { style: reduce ? {} : { rotateX, rotateY, transformPerspective: 900 }, onPointerMove: onMove, onPointerLeave: onLeave };
}

export function ListingCard({ listing }) {
  const img = listing.photos?.[0] ? fileUrl(listing.photos[0]) : null;
  const tilt = useTilt();
  return (
    <motion.div {...tilt} whileHover={{ y: -6 }} transition={{ type: "spring", stiffness: 260, damping: 22 }} className="h-full">
      <Link to={`/listing/${listing.id}`} data-testid={`listing-card-${listing.id}`} draggable
        onDragStart={(e) => { e.dataTransfer.setData("application/x-vorra-car", JSON.stringify(snapshot(listing))); e.dataTransfer.effectAllowed = "copy"; setDragging(true); }}
        onDragEnd={() => setDragging(false)}
        className="group block h-full bg-white rounded-2xl border border-navy/10 overflow-hidden shadow-[0_1px_2px_rgba(22,36,47,0.04)] hover:shadow-[0_24px_50px_-20px_rgba(22,36,47,0.35)] transition-shadow duration-300">
        <div className="relative aspect-[16/10] bg-[#E4EEF2] overflow-hidden">
          {img ? (
            <img src={img} alt={`${listing.make} ${listing.model}`} className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-110" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-slatev/50"><ImageOff className="w-8 h-8" /></div>
          )}
          <div className="absolute top-3 right-3"><CompareButton listing={listing} /></div>
          <div className="absolute top-3 left-3 flex flex-wrap gap-1.5">
            {listing.is_featured && <span className="inline-flex items-center gap-1 rounded-full bg-amber-400 text-navy text-[11px] font-bold px-2 py-0.5" data-testid={`featured-badge-${listing.id}`}><Star className="w-3 h-3" />Featured</span>}
            {listing.verified ? <TrustBadge type="verified" /> : listing.data_checked && <TrustBadge type="data_checked" />}
            {listing.mileage_warning && <TrustBadge type="mileage" />}
          </div>
        </div>
        <div className="p-5">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h3 className="font-heading font-bold text-lg leading-tight truncate text-navy">{listing.make} {listing.model}</h3>
              <p className="text-sm text-slatev truncate">{listing.variant || listing.body_type}</p>
            </div>
            <span className="font-heading font-extrabold text-lg text-teal shrink-0">{gbp(listing.price)}</span>
          </div>
          <div className="flex items-center gap-4 mt-4 pt-4 border-t border-navy/5 text-xs text-slatev flex-wrap">
            <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{listing.year}</span>
            <span className="flex items-center gap-1"><Gauge className="w-3.5 h-3.5" />{Number(listing.mileage).toLocaleString()} mi</span>
            {listing.fuel && <span className="flex items-center gap-1"><Fuel className="w-3.5 h-3.5" />{listing.fuel}</span>}
            {listing.location && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{listing.location}{listing.distance_miles != null && ` · ${listing.distance_miles} mi`}</span>}
          </div>
        </div>
      </Link>
    </motion.div>
  );
}
